import React, { useMemo } from "react";

import { Card, hasDimensions, RTEContent } from "shared/components";
import { Spacer } from "shared/layout";
import { getStageActions, useVenueRoute } from "Venues/utils";
import { Stage } from "shared/assets/icons";
import { StageCapacities, StageDimensions } from "Venues/components";
import { IStage } from "Venues/types";
import { IUserPermissions } from "shared/utils";
import { useStagePageContext, useVenuePageContext } from "Venues/contexts";

interface InfoCardProps {
  stage: IStage;
  venueId: string;
  openDeleteStageModal: (id: string) => void;
  userPermissions: IUserPermissions;
}

export const InfoCard: React.FC<InfoCardProps> = ({
  stage,
  userPermissions,
  openDeleteStageModal,
}) => {
  const { brandColor } = useVenuePageContext();
  const { openSuggestStageEditModal } = useStagePageContext();
  const { pushRoute } = useVenueRoute();

  const stageHasInfo = useMemo(() => {
    const {
      info,
      stageCapacities,
      dimensions,
      stageWeightPerSquareMeter,
      distanceFromLoadingToStage,
    } = stage || {};
    const checkPropertyExists = (property: any) => property !== null && property !== undefined;
    return {
      info: !!info,
      stageCapacities: checkPropertyExists(stageCapacities) && stageCapacities?.length !== 0,
      distanceFromLoadingToStage: checkPropertyExists(distanceFromLoadingToStage),
      stageWeightPerSquareMeter: checkPropertyExists(stageWeightPerSquareMeter),
      dimensions: hasDimensions(dimensions?.room) || hasDimensions(dimensions?.stage),
    };
  }, [stage]);

  const stageHasInfoCardContent = useMemo(
    () =>
      stageHasInfo.info ||
      stageHasInfo.stageCapacities ||
      stageHasInfo.dimensions ||
      stageHasInfo.stageWeightPerSquareMeter ||
      stageHasInfo.distanceFromLoadingToStage,
    [stageHasInfo]
  );

  const actions = useMemo(() => {
    if (stage) {
      return getStageActions({
        userPermissions,
        stageId: stage.id,
        openDeleteStageModal,
        openSuggestStageEditModal,
        pushRoute,
      });
    }
  }, [stage, userPermissions, openDeleteStageModal, openSuggestStageEditModal, pushRoute]);

  return (
    <Card
      icon={Stage}
      title={stage.title}
      actions={actions}
      accentColor={brandColor}
      actionDataTestId="stage_info_actions"
    >
      {stageHasInfoCardContent && (
        <Spacer size="l">
          <RTEContent content={stage.info} />

          {stageHasInfo.stageCapacities && <StageCapacities capacities={stage.stageCapacities} />}
          {(stageHasInfo.distanceFromLoadingToStage ||
            stageHasInfo.stageWeightPerSquareMeter ||
            stageHasInfo.dimensions) && (
            <StageDimensions
              stageWeightPerSquareMeter={
                stageHasInfo.stageWeightPerSquareMeter ? stage.stageWeightPerSquareMeter : undefined
              }
              distanceFromLoadingToStage={
                stageHasInfo.distanceFromLoadingToStage
                  ? stage.distanceFromLoadingToStage
                  : undefined
              }
              dimensions={stage.dimensions}
            />
          )}
        </Spacer>
      )}
    </Card>
  );
};
